import React, { useEffect, useState } from 'react';
import { ChevronDown, Megaphone } from 'lucide-react';
import { listMyAnnouncements, type Announcement } from '../../lib/announcements';
import { Panel, PanelHead } from '../lms/ui';

const AnnouncementsCard: React.FC<{ prominent?: boolean; className?: string }> = ({
  prominent = false,
  className = '',
}) => {
  const [items, setItems] = useState<Announcement[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    void listMyAnnouncements().then((rows) => {
      setItems(rows);
      if (rows[0]) setExpanded(rows[0].id);
    });
  }, []);

  if (items.length === 0) return null;

  return (
    <Panel
      className={`overflow-hidden ${
        prominent ? 'border-blue-200 shadow-[0_18px_40px_-30px_rgba(29,78,216,0.5)]' : ''
      } ${className}`}
    >
      {prominent ? (
        <div className="flex items-start gap-3 border-b border-blue-100 bg-gradient-to-r from-blue-50 via-sky-50 to-white px-5 py-4">
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-blue-600 text-white shadow-sm">
            <Megaphone className="h-4 w-4" strokeWidth={1.7} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em] text-blue-700">
                Notice board
              </p>
              <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">
                {items.length} on file
              </span>
            </div>
            <p className="mt-1 text-[13px] font-semibold leading-5 text-ink">Updates from the LSEM desk</p>
          </div>
        </div>
      ) : (
        <PanelHead
          title="Notices"
          action={<span className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink-2">{items.length} on file</span>}
        />
      )}
      <div className="divide-y divide-rule">
        {items.slice(0, 3).map((a) => {
          const isOpen = expanded === a.id;
          return (
            <div key={a.id}>
              <button
                onClick={() => setExpanded(isOpen ? null : a.id)}
                className="flex w-full items-start justify-between gap-3 px-5 py-3.5 text-left hover:bg-paper"
                aria-expanded={isOpen}
              >
                <span className="min-w-0">
                  <span
                    className={`block leading-5 text-ink ${
                      prominent ? 'text-[14px] font-semibold' : 'text-[12.5px] font-medium'
                    }`}
                  >
                    {a.title}
                  </span>
                  <span className="mt-0.5 block font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">
                    {a.course_id ? 'Programme update' : 'All students'} ·{' '}
                    {new Date(a.created_at).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                </span>
                <ChevronDown className={`mt-0.5 h-3.5 w-3.5 flex-none text-ink-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} strokeWidth={1.5} />
              </button>
              {isOpen && a.body && (
                <p
                  className={`whitespace-pre-wrap border-t border-rule/70 bg-paper/60 px-5 py-3 leading-5 text-ink-2 ${
                    prominent ? 'text-[13px]' : 'text-[12.5px]'
                  }`}
                >
                  {a.body}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </Panel>
  );
};

export default AnnouncementsCard;
